import { HelpHintWindow } from "@/components/ui/HintWindow";
import { PanelGrid } from "@/components/ui/Panel";
import { useLanguage } from "@/context/LanguageContext";
import {
  ArrowsPointingOutIcon,
  CalendarDaysIcon,
  InformationCircleIcon,
} from "@heroicons/react/24/outline";
import { DestructiveTrashIcon } from "@/components/ui/DestructiveTrashIcon";

import { PortfolioPageInsightItem } from "@/components/portfolio/portfolio-page/PortfolioPageInsightItem";

type ChartLineHintWindowProps = {
  isOpen: boolean;
  onToggle: () => void;
};

export function ChartLineHintWindow({
  isOpen,
  onToggle,
}: ChartLineHintWindowProps) {
  const { language } = useLanguage();
  const copy = {
    cs: {
      help: "Nápověda",
      title: "Vývoj ceny",
      description: "Kompaktní přehled trendu, metrik a detailu nabídky.",
      hideHelp: "Skrýt nápovědu",
      metricsTitle: "Přepínání metrik",
      metricsText:
        "Kliknutím na kartu změníte zobrazenou křivku a rychle porovnáte cenu i odhad nájmu.",
      periodTitle: "Práce s obdobím",
      periodText:
        "Pomocí filtru nad výsledky zvolíte dnešek, poslední týdny nebo celou dostupnou historii.",
      detailTitle: "Detail nabídky",
      detailText:
        "Najetím myší uvidíte cenu a kliknutím si nabídku připnete pod graf.",
      unsuitableTitle: "Nevyhovující nabídka",
      unsuitableText:
        "Nabídky, které výrazně vybočují z průměru můžete jednoduše smazat. Ať už v nájmu nebo prodeji.",
    },
    en: {
      help: "Help",
      title: "Price development",
      description: "Compact overview of the trend, metrics, and offer detail.",
      hideHelp: "Hide help",
      metricsTitle: "Metric switching",
      metricsText:
        "Click a card to change the displayed curve and quickly compare price and rent estimate.",
      periodTitle: "Working with the period",
      periodText:
        "Use the filter above the results to select today, recent weeks, or all available history.",
      detailTitle: "Offer detail",
      detailText:
        "Hover to see the price and click to pin the offer below the chart.",
      unsuitableTitle: "Unsuitable offer",
      unsuitableText:
        "Offers that deviate strongly from the average can be deleted easily, whether for rent or sale.",
    },
  }[language];

  if (!isOpen) return null;

  return (
    <HelpHintWindow
      eyebrow={copy.help}
      title={copy.title}
      description={copy.description}
      closeLabel={copy.hideHelp}
      onClose={onToggle}
    >
      <PanelGrid className="xl:grid-cols-4">
        <PortfolioPageInsightItem
          icon={ArrowsPointingOutIcon}
          title={copy.metricsTitle}
          text={copy.metricsText}
        />
        <PortfolioPageInsightItem
          icon={CalendarDaysIcon}
          title={copy.periodTitle}
          text={copy.periodText}
        />
        <PortfolioPageInsightItem
          icon={InformationCircleIcon}
          title={copy.detailTitle}
          text={copy.detailText}
        />
        <PortfolioPageInsightItem
          icon={DestructiveTrashIcon}
          title={copy.unsuitableTitle}
          text={copy.unsuitableText}
        />
      </PanelGrid>
    </HelpHintWindow>
  );
}
